import { useMemo, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import type { PropertyDetailsFragment } from '__generated__/athena';
import { buildCarouselPhotos } from 'components/marketplace/helpers';
import { removeAddressPreviewParams } from 'components/marketplace/helpers/useSearchQueries';
import { PhotoCarousel as MapPhotoCarousel } from 'components/marketplace/map/PhotoCarousel';
import { ShareModal } from 'components/marketplace/property';
import { MiniMapPropertySummary } from 'components/marketplace/property/PropertySection/MiniMapPropertySummary';

interface MiniPropertyDetailPageProps {
  property: PropertyDetailsFragment;
  onClose: () => void;
}
const SWIPE_THRESHOLD = 50;

export const MobileMiniPropertyDetailPage = ({
  property,
  onClose,
}: MiniPropertyDetailPageProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [swipeStartY, setSwipeStartY] = useState(null);
  const handleTouchStart = (e: any) => {
    const touch = e.touches[0];
    setSwipeStartY(touch.clientY);
  };

  const handleOnClose = () => {
    const urlParams = removeAddressPreviewParams();
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
    onClose();
  };

  const handleToggleShareModal = () => {
    setIsShareModalOpen((prev) => !prev);
  };

  // Handle the end of the touch and check if it's a swipe down
  const handleTouchEnd = (e: any) => {
    if (swipeStartY === null) return;

    const touch = e.changedTouches[0];
    const swipeDistance = touch.clientY - swipeStartY;

    if (swipeDistance > SWIPE_THRESHOLD) {
      handleOnClose();
    }
    setSwipeStartY(null);
  };

  const handleOnClick = () => {
    window.location.href = `/properties/${property?.slug}/aid_${property?.addressToken}`;
  };

  // Floor plans intentionally omitted here: the PhotoCarousel cover-crops
  // portrait plans. Tapping a photo navigates to the full PDP, where floor
  // plans are rendered uncropped in the gallery and Lightbox.
  const carouselPhotos = useMemo(
    () => buildCarouselPhotos(property),
    [property?.photos, property?.floorPlanPhotos],
  );

  return (
    <Col>
      <Col
        className="h-full w-full touch-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        key={`mobile-mini-pdp-${property?.addressToken}`}
      >
        <Box className="shrink-0 pb-6">
          <MapPhotoCarousel
            key={`mobile-carousel-${property?.addressToken}`}
            photos={carouselPhotos}
            onClose={handleOnClose}
            onOpenShareModal={handleToggleShareModal}
            isRounded
            onPhotoClick={handleOnClick}
          />
        </Box>
        <Col className="gap-2.5 px-5">
          <MiniMapPropertySummary property={property} />
          <Button
            analyticsName="mobile-map-pdp-link"
            href={`/properties/${property?.slug}/aid_${property?.addressToken}`}
            variant="secondary"
            aria-label="View home"
            className="w-full"
          >
            View home
          </Button>
        </Col>
      </Col>
      <ShareModal property={property} isOpen={isShareModalOpen} onClose={handleToggleShareModal} />
    </Col>
  );
};
